import { useEffect, useState } from "react";
import {
    Scales, ArrowDown, ArrowUp, DownloadSimple, Receipt,
    Stack, ArrowCounterClockwise, FunnelSimple,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

function currentFy() {
    const now = new Date();
    const y = now.getFullYear();
    const start = now.getMonth() >= 3 ? y : y - 1;
    return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

function fyOptions() {
    const cur = currentFy();
    const startYear = parseInt(cur.split("-")[0], 10);
    return Array.from({ length: 6 }, (_, i) => {
        const s = startYear - i + 1;
        return { value: `${s - 1}-${String(s % 100).padStart(2, "0")}`, label: `FY ${s - 1}-${String(s % 100).padStart(2, "0")}` };
    });
}

export default function VendorGstCompliance() {
    const [fy, setFy] = useState(currentFy());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const r = await api.get("/billing/reports/vendor-compliance/gst", { params: { fy } });
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load GST compliance report"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

    const csv = (which) => {
        const rows = which === "input" ? data?.input?.items : data?.output?.items;
        if (!rows || !rows.length) return;
        const cols = ["Source", "Code", "Txn No", "Txn Date", "Counterparty", "Taxable Value", "GST Amount"];
        const dataRows = rows.map((r) => [
            r.source_label, r.code, r.txn_number, r.txn_date,
            r.source === "customer_invoice" ? r.client_name : r.vendor_name,
            r.taxable_value, r.gst_amount,
        ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([cols.join(",") + "\n" + dataRows.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gst-${which}-${fy}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading && !data) {
        return (
            <div data-testid="vendor-gst-page-loading">
                <PageHeader eyebrow="Compliance · Net GST" title="Loading…" />
            </div>
        );
    }
    if (!data) return null;

    const s = data.summary || {};
    const isPayable = (s.net_gst || 0) >= 0;

    return (
        <div data-testid="vendor-gst-compliance-page">
            <PageHeader
                eyebrow={`Compliance · Net GST Liability · FY ${fy}`}
                title={
                    <span className="flex items-center gap-3">
                        <Scales size={22} weight="duotone" className="text-indigo-600" />
                        Net GST Liability
                    </span>
                }
                description="Lightweight reporting: Output GST collected from customer invoices minus Input GST paid on vendor bills + service invoices."
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="vendor-gst-summary">
                    <Kpi label="Output GST" value={formatINR(s.output_gst || 0)}
                         sub={`${s.output_count || 0} customer invoice${s.output_count === 1 ? "" : "s"}`}
                         icon={<ArrowUp size={11} weight="fill" />} tone="emerald" testid="kpi-output-gst" />
                    <Kpi label="Input GST" value={formatINR(s.input_gst || 0)}
                         sub={`${s.input_count || 0} vendor bill${s.input_count === 1 ? "" : "s"} + service invoices`}
                         icon={<ArrowDown size={11} weight="fill" />} tone="indigo" testid="kpi-input-gst" />
                    <div className={classJoin("reedos-card p-4 border-2",
                        isPayable ? "border-rose-200 bg-rose-50/40" : "border-emerald-200 bg-emerald-50/40")}
                        data-testid="kpi-net-gst">
                        <div className="reedos-eyebrow flex items-center gap-1.5">
                            <Scales size={11} weight="fill" /> {isPayable ? "Net GST Payable" : "Net GST Credit"}
                        </div>
                        <div className={classJoin("font-display font-bold reedos-data text-3xl mt-1 leading-none",
                            isPayable ? "text-rose-700" : "text-emerald-700")}>
                            {formatINR(Math.abs(s.net_gst || 0))}
                        </div>
                        <div className="text-[11px] text-slate-600 mt-1">
                            {isPayable
                                ? "Output exceeds Input → liability to government"
                                : "Input exceeds Output → input tax credit available"}
                        </div>
                    </div>
                </div>

                {/* Filter bar */}
                <div className="reedos-card p-4" data-testid="vendor-gst-filters">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                        <FilterSelect label="Financial Year" value={fy} onChange={setFy} options={fyOptions()} testid="filter-fy" />
                        <div className="sm:col-span-2 flex items-center gap-2 justify-end">
                            <div className="text-[11px] text-slate-500 mr-2">FY window: <span className="font-mono">{data.window?.from} → {data.window?.to}</span></div>
                            <Button variant="outline" size="sm" onClick={() => setFy(currentFy())} className="gap-1.5" data-testid="filter-reset-btn">
                                <ArrowCounterClockwise size={12} /> Current FY
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Tabs: Input vs Output drilldowns */}
                <Tabs defaultValue="input" data-testid="vendor-gst-tabs">
                    <TabsList className="grid grid-cols-2 max-w-sm">
                        <TabsTrigger value="input" data-testid="tab-input-gst">Input GST ({s.input_count || 0})</TabsTrigger>
                        <TabsTrigger value="output" data-testid="tab-output-gst">Output GST ({s.output_count || 0})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="input">
                        <DrillGrid
                            rows={data.input?.items || []}
                            totalGst={data.input?.total_gst || 0}
                            counterpartyLabel="Vendor"
                            counterpartyField="vendor_name"
                            counterpartyCodeField="vendor_code"
                            csv={() => csv("input")}
                            testid="input-gst-grid"
                        />
                    </TabsContent>

                    <TabsContent value="output">
                        <DrillGrid
                            rows={data.output?.items || []}
                            totalGst={data.output?.total_gst || 0}
                            counterpartyLabel="Client"
                            counterpartyField="client_name"
                            counterpartyCodeField={null}
                            csv={() => csv("output")}
                            testid="output-gst-grid"
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function DrillGrid({ rows, totalGst, counterpartyLabel, counterpartyField, counterpartyCodeField, csv, testid }) {
    return (
        <div className="reedos-card overflow-hidden mt-4" data-testid={testid}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <FunnelSimple size={14} className="text-slate-500" />
                <div className="font-display font-bold text-sm">{rows.length} transactions</div>
                <div className="text-[11px] text-slate-500 ml-2">GST Total: <span className="font-mono font-bold">{formatINR(totalGst)}</span></div>
                <div className="ml-auto">
                    <Button variant="outline" size="sm" onClick={csv} disabled={!rows.length} className="gap-1.5" data-testid={`${testid}-export-btn`}>
                        <DownloadSimple size={13} /> CSV
                    </Button>
                </div>
            </div>
            {rows.length === 0 ? (
                <EmptyState title="No GST transactions" description="No taxable transactions found for this filter." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left">
                                <th className="px-4 py-2 reedos-eyebrow text-[9px]">Source</th>
                                <th className="px-4 py-2 reedos-eyebrow text-[9px]">Code</th>
                                <th className="px-4 py-2 reedos-eyebrow text-[9px]">Txn No · Date</th>
                                <th className="px-4 py-2 reedos-eyebrow text-[9px]">{counterpartyLabel}</th>
                                <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Taxable Value</th>
                                <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">GST</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={`${r.source}-${r.id}`} className="border-t border-slate-100 reedos-row-hover" data-testid={`gst-row-${r.id}`}>
                                    <td className="px-4 py-2.5 text-xs">
                                        <span className={classJoin("inline-block px-1.5 py-0.5 rounded font-semibold text-[10px]",
                                            r.source === "vendor_bill" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" :
                                                r.source === "service_invoice" ? "bg-violet-50 text-violet-700 border border-violet-200" :
                                                    "bg-emerald-50 text-emerald-700 border border-emerald-200")}>
                                            {r.source_label}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs font-mono font-semibold text-slate-900">{r.code}</td>
                                    <td className="px-4 py-2.5 text-xs">
                                        <div className="font-mono text-slate-700">{r.txn_number}</div>
                                        <div className="text-[10px] text-slate-500">{r.txn_date}</div>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs">
                                        <div className="font-semibold text-slate-900 truncate max-w-[180px]">{r[counterpartyField] || "—"}</div>
                                        {counterpartyCodeField && r[counterpartyCodeField] && (
                                            <div className="font-mono text-[10px] text-slate-500">{r[counterpartyCodeField]}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(r.taxable_value)}</td>
                                    <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-indigo-700">{formatINR(r.gst_amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t border-slate-200">
                            <tr className="font-semibold">
                                <td colSpan={4} className="px-4 py-3 text-right reedos-eyebrow">Total GST</td>
                                <td className="px-4 py-3 text-right reedos-data text-slate-700">—</td>
                                <td className="px-4 py-3 text-right reedos-data text-indigo-700 font-bold">{formatINR(totalGst)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        indigo: "text-indigo-700",
        violet: "text-violet-700",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div>}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid={testid}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
